import { useEffect, useRef, useState } from "react";
import { Camera, Check, ImagePlus, Upload } from "lucide-react";
import { Brand } from "./Brand";

export function GuestSubmitPage({ token }: { token: string }) {
  const [eventName, setEventName] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(0);
  const [progress, setProgress] = useState("");
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const photosInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    fetch(`/api/capture/${token}`, { credentials: "omit" })
      .then(async (res) => {
        if (!res.ok)
          throw new Error("This event submission link is unavailable.");
        const data = await res.json();
        if (live) setEventName(data.name);
      })
      .catch((e) => {
        if (live) {
          setUnavailable(true);
          setError((e as Error).message);
        }
      });
    return () => {
      live = false;
    };
  }, [token]);

  async function submit(files: FileList | null) {
    if (!files?.length || loading || unavailable) return;
    const chosen = Array.from(files);
    setLoading(true);
    setError("");
    let completed = 0;
    for (const [index, file] of chosen.entries()) {
      if (file.size > 80 * 1024 * 1024) {
        setError(
          `${file.name} is over the 80 MB limit. Choose a smaller photo.`,
        );
        break;
      }
      if (!file.type.startsWith("image/")) {
        setError(`${file.name} is not an image. Choose photo files only.`);
        break;
      }
      setProgress(`Uploading ${index + 1} of ${chosen.length}…`);
      const body = new FormData();
      body.append("file", file);
      try {
        const response = await fetch(`/api/capture/${token}/upload`, {
          method: "POST",
          credentials: "omit",
          body,
          signal: AbortSignal.timeout(120000),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 404) setUnavailable(true);
          throw new Error(result.error || "Upload failed. Please try again.");
        }
        completed++;
      } catch (e) {
        setError(
          `${(e as Error).message} ${completed ? `${completed} photo${completed === 1 ? "" : "s"} uploaded.` : ""}`,
        );
        break;
      }
    }
    if (completed) setSent((count) => count + completed);
    setProgress("");
    setLoading(false);
  }

  return (
    <main className="capture-page guest-submit-page">
      <header className="capture-header">
        <Brand />
        <span className="pill">GUEST PHOTO UPLOAD</span>
      </header>
      <div className="capture-intro">
        <p className="eyebrow">SHARE THE MOMENT</p>
        <h1>
          {eventName || (unavailable ? "Link unavailable" : "Loading event…")}
        </h1>
        <p>Take a photo now or choose photos from your phone.</p>
      </div>
      <section className="capture-card guest-submit-card">
        {sent > 0 && (
          <div className="capture-success" role="status">
            <span>
              <Check size={28} />
            </span>
            <h2>Thanks for sharing!</h2>
            <p>
              {sent} photo{sent === 1 ? "" : "s"} added to the event.
            </p>
          </div>
        )}
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        {progress ? (
          <p className="guest-upload-progress" role="status">
            {progress}
          </p>
        ) : null}
        <div className="guest-submit-actions">
          <button
            className="primary"
            disabled={!eventName || loading || unavailable}
            onClick={() => cameraInput.current?.click()}
          >
            <Camera size={20} /> Take a photo
          </button>
          <button
            disabled={!eventName || loading || unavailable}
            onClick={() => photosInput.current?.click()}
          >
            <ImagePlus size={20} /> Upload photos
          </button>
        </div>
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            void submit(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={photosInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            void submit(e.target.files);
            e.target.value = "";
          }}
        />
        <p className="capture-help">
          Stay connected until your photos finish uploading. Photos go directly
          to the event library.
        </p>
      </section>
      <footer className="capture-footer">
        <Upload size={14} /> No account needed
      </footer>
    </main>
  );
}
